import React, { useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { IconContext } from 'react-icons';
import {
  IoEyeOutline,
  IoEyeOffOutline,
  IoWarningOutline,
} from 'react-icons/io5';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const Input = ({
  type,
  iconLeft,
  iconLeftBool,
  iconRight,
  iconRightBool,
  iconClassError,
  classNames,
  onPhoneChange,
  autoFocus,
  required,
  country,
  ...rest
}) => {
  const [isIconGreen, setIsIconGreen] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const eyeIcon = isPasswordVisible ? IoEyeOutline : IoEyeOffOutline;

  const renderIcon = (icon) => {
    const IconComponent = icon;
    return <IconComponent />;
  };

  const handlePhoneChange = (value, country) => {
    if (country && country.format) {
      const validNumberCount = (country.format.match(/\./g) || []).length;
      setIsIconGreen(value.length === validNumberCount);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const passwordInputStyle = clsx(classNames, {
    'w-full pr-4 py-2 text-center border border-solid shadow-lg rounded-full placeholder-gray-400 focus:outline-none': true,
    'pl-12': type != 'text',
    'text-red-500': iconClassError,
    'text-black': !iconClassError,
  });

  return (
    <div className="relative w-full">
      {iconLeftBool && (
        <span className="absolute inset-y-0 left-0 flex items-center pl-2">
          <IconContext.Provider value={{ size: '1.5em', color: '#ABABAB' }}>
            {renderIcon(iconLeft)}
          </IconContext.Provider>
        </span>
      )}
      {type === 'tel' ? (
        <PhoneInput
          inputProps={{
            name: rest.name,
            autoFocus: autoFocus,
            required: required,
            autoFormat: true,
            maxLength: rest.maxLength,
            className: `
                w-full
                py-2
                pr-10
                pl-12
                text-center
                border-solid
                border
                rounded-full
                text-gray-900
                placeholder-gray-400
                shadow-lg
                focus:outline-none
              `,
          }}
          value={rest.value}
          placeholder={rest.placeholder}
          onChange={(value, countryData, event) => {
            handlePhoneChange(value, countryData);
            if (onPhoneChange) {
              onPhoneChange(value, countryData, event);
            }
            if (rest.onChange) {
              rest.onChange(event);
            }
          }}
          onBlur={rest.onBlur}
          country={country}
          buttonStyle={{
            backgroundColor: '#fff',
            borderRadius: '100%',
            border: 'none',
            borderLeft: 'none',
            position: 'absolute',
            top: '50%',
            left: '0',
            transform: 'translateY(-50%)',
            marginLeft: '0.5rem',
          }}
        />
      ) : (
        <input
          type={type === 'password' && isPasswordVisible ? 'text' : type}
          className={passwordInputStyle}
          autoFocus={autoFocus}
          required={required}
          {...rest}
        />
      )}
      {iconRightBool && (
        <span
          className="absolute inset-y-0 text-center right-0 flex items-center pr-2"
          onClick={type === 'password' ? handleTogglePasswordVisibility : null}
        >
          <IconContext.Provider
            value={{
              size: '1.5em',
              color: `${isIconGreen ? '#00FF00' : iconClassError ? '#FF0000' : '#ABABAB'}`,
            }}
          >
            {type === 'password' && !iconClassError
              ? renderIcon(eyeIcon)
              : type !== 'password' && !iconClassError
                ? renderIcon(iconRight)
                : renderIcon(IoWarningOutline)}
          </IconContext.Provider>
        </span>
      )}
    </div>
  );
};

Input.propTypes = {
  type: PropTypes.oneOf(['text', 'email', 'password', 'tel']),
  iconLeft: PropTypes.object,
  iconRight: PropTypes.object,
  // `tel` only: receives (value, countryData, event) so the caller can read
  // `countryData.dialCode` — the plain `onChange` event carries the formatted
  // number with the dial code baked in and no way to split it back out.
  onPhoneChange: PropTypes.func,
  autoFocus: PropTypes.bool,
  required: PropTypes.bool,
  // `tel` only: ISO-2, lowercase (react-phone-input-2 convention).
  country: PropTypes.string,
};

Input.defaultProps = {
  type: 'text',
  iconLeft: null,
  iconRight: null,
  onPhoneChange: null,
  autoFocus: false,
  required: false,
  country: 'us',
};

export default Input;
